import { Navigate, Route } from 'react-router-dom';
import { HomePage } from './components/home/HomePage';
import { DomainPage } from './components/home/DomainPage';
import { SystemPage } from './components/home/SystemPage';
import { CatalogHealthPage } from './components/catalog-health/CatalogHealthPage';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogHealthReadPermission } from './permissions';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import {
  colorVariants,
  createUnifiedTheme,
  genPageTheme,
  pageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { gitlabAuthApiRef } from '@backstage/core-plugin-api';
import {
  ContributePage,
  EditTechTalkPage,
} from '@internal/backstage-plugin-tech-talk';
import { frTranslationResources } from './translations';

// Thèmes par défaut, sauf l'en-tête TechDocs (thème « documentation ») passé
// du rose par défaut au teal des autres pages.
const kaizenPageTheme = {
  ...pageTheme,
  documentation: genPageTheme({
    colors: colorVariants.teal,
    shape: shapes.wave2,
  }),
};
const lightTheme = createUnifiedTheme({
  palette: palettes.light,
  pageTheme: kaizenPageTheme,
});
const darkTheme = createUnifiedTheme({
  palette: palettes.dark,
  pageTheme: kaizenPageTheme,
});

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(scaffolderPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          {
            id: 'gitlab-auth-provider',
            title: 'GitLab',
            message: 'Se connecter avec GitLab',
            apiRef: gitlabAuthApiRef,
          },
        ]}
      />
    ),
  },
  // Mêmes thèmes que par défaut, redéclarés pour franciser leurs titres
  // (affichés dans Paramètres > Apparence).
  themes: [
    {
      id: 'light',
      title: 'Clair',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={lightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Sombre',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={darkTheme} children={children} />
      ),
    },
  ],
  // Les overrides remplacent les textes anglais par défaut ; pas de
  // defaultLanguage/availableLanguages pour rester mono-langue sans sélecteur.
  __experimentalTranslations: {
    resources: frTranslationResources,
  },
});

export const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/domain/:domainName" element={<DomainPage />} />
    <Route path="/system/:systemName" element={<SystemPage />} />
    <Route
      path="/catalog-health"
      element={
        <RequirePermission permission={catalogHealthReadPermission}>
          <CatalogHealthPage />
        </RequirePermission>
      }
    />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/contribuer" element={<ContributePage />} />
    {/* Former "Depuis Sharepoint" form, now covered by /contribuer */}
    <Route
      path="/tech-talks/new"
      element={<Navigate to="/contribuer" replace />}
    />
    <Route path="/tech-talks/:name/edit" element={<EditTechTalkPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
